import { Component, OnInit, Input, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  LoyaltyLimitType,
  TermsAndCondtionsType,
  LoyaltyCategoryType,
  BalanceQuery,
  config,
  LottoV2Query,
  LottoDetails,
  LOTTO_STATE,
  LottoUIStatus,
  LottoFacade,
  CustomField,
  CustomFieldValidationType,
  CustomFieldType,
} from '@fgb/core';
import { map, Observable } from 'rxjs';
import { ScreenType } from '@fgb/portal-component-library/src/lib/directives';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { FGBSharedModule } from '@fgb/portal-component-library/src/lib/shared';
import { ErrorsModule } from '@fgb/portal-component-library/src/lib/shared/errors';
import { NumberInputModule } from '@fgb/portal-component-library/src/lib/shared/number-input';
import { ShortLongDescriptionModule } from '@fgb/portal-component-library/src/lib/shared/short-long-description';
import { PipesModule } from '@fgb/portal-component-library/src/lib/pipes';
import { LoyaltyIconModule } from '@fgb/portal-component-library/src/lib/shared/loyalty-icon';
import { CountdownModule } from '@fgb/portal-component-library/src/lib/shared/countdown';
import { DirectivesModule } from '@fgb/portal-component-library/src/lib/directives';
import { SocialShareModalModule } from '@fgb/portal-component-library/src/lib/shared/social-share-modal';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CountdownV2Component } from '@fgb/portal-component-library/src/lib/shared/countdown-v2';
import { CustomFieldComponent } from '@fgb/portal-component-library/src/lib/shared/custom-field';
import { QuantityFieldComponent } from '@fgb/portal-component-library/src/lib/shared/quantity-field';
import { LottoPurchaseConfirmationV2Component } from './lotto-purchase-confirmation-v2/lotto-purchase-confirmation-v2.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'fgbcl-lotto-details-v2',
  standalone: true,
  imports: [
    CommonModule,
    ErrorsModule,
    LoyaltyIconModule,
    CountdownModule,
    CountdownV2Component,
    NumberInputModule,
    PipesModule,
    ShortLongDescriptionModule,
    FormsModule,
    ReactiveFormsModule,
    DirectivesModule,
    FGBSharedModule,
    SocialShareModalModule,
    QuantityFieldComponent,
    CustomFieldComponent,
    LottoPurchaseConfirmationV2Component,
  ],
  templateUrl: './lotto-details-v2.component.html',
  styleUrl: './lotto-details-v2.component.scss',
})
export class LottoDetailsV2Component implements OnInit {
  @Input() lottoId: number;
  @Input() completeRedirect: string = '/rewards/raffles';
  @Input() maxAllowedQuantity = 100; // This is an arbitrary cap for maximum quantity when stock is unlimited. Adjust as necessary.

  private readonly lottoFacade = inject(LottoFacade);
  private readonly lottoItemQuery = inject(LottoV2Query);
  private readonly balanceQuery = inject(BalanceQuery);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly modalService = inject(NgbModal);

  readonly LoyaltyLimitType = LoyaltyLimitType;
  readonly TermsAndCondtionsType = TermsAndCondtionsType;
  readonly screenSize = ScreenType;
  readonly loyaltyCategoryType = LoyaltyCategoryType.Lotto;
  readonly LOTTO_STATE = LOTTO_STATE;

  maxQuantity: number;
  minQuantity = 1;

  context$!: Observable<ReturnType<typeof this.createContext> | null>;
  lottoDetailsForm: FormGroup;
  lottoStatus: Signal<LottoUIStatus | undefined>;

  /* ================================
   * Lifecycle Hooks
   * ================================ */

  ngOnInit(): void {
    this.lottoFacade.fetchLottoById(this.lottoId);
    this.lottoStatus = this.lottoItemQuery.getStatusById(this.lottoId);
    this.context$ = this.lottoItemQuery.selectLottoById(this.lottoId).pipe(map((lotto) => this.buildContext(lotto)));
  }

  /* ================================
   * Public Methods
   * ================================ */

  /** Whether the enter button is disabled: the quantity is disabled, or it exceeds the item limit or the available quantity. */
  get isEnterDisabled(): boolean {
    const errors = this.lottoDetailsForm?.errors;
    return (
      !!this.lottoDetailsForm?.get('quantity')?.disabled ||
      !!errors?.['limitExceeded'] ||
      !!errors?.['productLimitExceeded'] ||
      !!errors?.['maxQuantityExceeded']
    );
  }

  /** Validates the form and opens the purchase confirmation modal. */
  openConfirmation(lotto: LottoDetails): void {
    this.lottoDetailsForm.markAllAsTouched();
    if (!this.lottoDetailsForm.valid) return;

    const { quantity, customFields } = this.lottoDetailsForm.value;

    // Api expects checkbox values to be strings, so we convert them accordingly.
    const mappedCustomFields = customFields?.map((field: any) => ({
      ...field,
      customFieldValue:
        field.customValidationType === CustomFieldValidationType.Checkbox ? `${field.customFieldValue}` : field.customFieldValue,
    }));

    const modal = this.modalService.open(LottoPurchaseConfirmationV2Component, {
      ariaLabelledBy: 'confirmation-title',
      centered: true,
      windowClass: 'reward-confirm-modal',
    });
    modal.componentInstance.lottoItem = lotto;
    modal.componentInstance.customFields = mappedCustomFields;
    modal.componentInstance.quantity = quantity;
  }

  /** Handles changes to the acceptance of terms and conditions. Enables or disables the quantity input based on the acceptance state. */
  onAcceptTermsChange(acceptTerms: boolean): void {
    const quantityControl = this.lottoDetailsForm.get('quantity');

    if (!quantityControl) return;

    acceptTerms ? quantityControl.enable({ emitEvent: false }) : quantityControl.disable({ emitEvent: false });
  }

  /** Redirects to the redirect page when the lotto ends. */
  lottoEnd() {
    if (this.modalService.hasOpenModals()) {
      this.modalService.dismissAll();
    }
    this.router.navigate([this.completeRedirect]);
  }

  /** Refreshes the lotto details by fetching the latest data. */
  refresh(): void {
    this.lottoFacade.fetchLottoById(this.lottoId);
  }

  /* ================================
   * Private Helpers
   * ================================ */

  /** Builds the context object and initializes related properties for the given marketplace item. */
  private buildContext(item: LottoDetails | undefined) {
    if (!item) return null;
    const context = this.createContext(item);
    this.assignMaxQuantity(context);
    this.applyFormDisabledState(context);
    this._initializeForm(context);
    return context;
  }

  /** Creates a context object for the given marketplace item. */
  private createContext(item: LottoDetails) {
    return {
      item,
      isLimitedItem: this.getLimitedState(item),
      isFormDisabled: false,
    };
  }

  /** Gets the limited state of the item based on its limit type and count. */
  private getLimitedState(item: LottoDetails): boolean {
    return item.LimitType !== LoyaltyLimitType.NoLimit && item.LimitCount > 0;
  }

  /** Determines the maximum allowable quantity based on stock availability and applicable business constraints. */
  private assignMaxQuantity(ctx: ReturnType<typeof this.createContext>): void {
    const stockCount = ctx.item.AvailableCount ?? 0;
    const quantityLimitFromFunds = this.getQuantityLimitFromFunds(ctx);
    const capAmount = stockCount > 0 ? stockCount : this.maxAllowedQuantity; // If stock is available, use it as the cap; otherwise, use the predefined max allowed quantity
    this.maxQuantity = Math.min(quantityLimitFromFunds, capAmount);
  }

  /** Calculates the maximum redeemable quantity based on the user's available balance and item unit value. */
  private getQuantityLimitFromFunds(ctx: ReturnType<typeof this.createContext>): number {
    const balance = this.balanceQuery.getPurse(config.purseConfig.virtualLoyalty) ?? 0;
    const purchasePrice = ctx.item.PurchasePrice ?? 0;

    if (purchasePrice <= 0) return Number.MAX_SAFE_INTEGER;

    return Math.max(0, Math.floor(balance / purchasePrice));
  }

  /** Applies the disabled state to the form based on item attributes, terms, and quantity constraints. */
  private applyFormDisabledState(ctx: ReturnType<typeof this.createContext>): void {
    if (ctx.isLimitedItem) {
      const remaining = ctx.item.LimitCount - ctx.item.NumberOfTicketsPurchasedByAccount;
      if (remaining <= 0) {
        ctx.isFormDisabled = true;
        return;
      }
    }
    // Disable form if it's an attribute item without a selected attribute, if it's an item with terms & conditions, or if the max quantity is 0 or less
    ctx.isFormDisabled = !!ctx.item.ShowTermsAndConditions || this.maxQuantity <= 0;
  }

  /**
   * Initializes the reactive form for the lotto detail view.
   *
   * We use Angular Reactive Forms here intentionally — all validation, disabled states, and field rules
   * are declared in one place, making the form the single source of truth.
   */
  private _initializeForm(ctx: ReturnType<typeof this.createContext>): void {
    const isQuantityDisabled = ctx.isFormDisabled;
    const isTermsDisabled = !ctx.item.ShowTermsAndConditions; // disable terms acceptance if the item does not have terms and conditions
    this.lottoDetailsForm = this.fb.group(
      {
        quantity: [{ value: 1, disabled: isQuantityDisabled }, [Validators.required, Validators.min(this.minQuantity)]],
        customFields: this.fb.array(
          ctx.item.CustomFields ? ctx.item.CustomFields.map((field: CustomField) => this._initializeCustomFieldForm(field)) : []
        ),
        acceptTerms: [{ value: false, disabled: isTermsDisabled }, [Validators.requiredTrue]],
      },
      { validators: [maxQuantityValidator(this.maxQuantity), qtyMembershipLimitValidator(ctx.item)] }
    );
  }

  /** Initializes the form group for a custom field. */
  private _initializeCustomFieldForm(field: CustomField): FormGroup {
    let validators = [];
    const isCheckbox = field.FieldType === CustomFieldType.Checkbox;

    if (field.IsRequired) {
      validators.push(isCheckbox ? Validators.requiredTrue : Validators.required);
    }
    if (field.CharacterLimit > 0) {
      validators.push(Validators.maxLength(field.CharacterLimit));
    }
    if (field.ValidationRegEx != '') {
      validators.push(Validators.pattern(field.ValidationRegEx));
    }
    return this.fb.group({
      customFieldId: [field.CustomFieldId],
      customFieldName: [field.DisplayName],
      customFieldValue: [isCheckbox ? false : '', validators],
      customValidationType: [field.ValidationType],
    });
  }
}

/* ================================
 * Form Validators
 * ================================ */

/** Validates that the quantity does not exceed the membership limit. */
function qtyMembershipLimitValidator(item: LottoDetails) {
  return (group: AbstractControl): ValidationErrors | null => {
    const quantity = group.get('quantity')?.value;
    // Product limit validation
    if (item.LimitType !== LoyaltyLimitType.NoLimit && item.LimitCount > 0) {
      const maxQuantity = item.LimitCount - item.NumberOfTicketsPurchasedByAccount;
      if (maxQuantity <= 0) return { limitExceeded: true };
      return quantity > maxQuantity && maxQuantity > 0
        ? { productLimitExceeded: { limit: maxQuantity, actual: quantity, type: item.LimitType } }
        : null;
    }
    return null;
  };
}

/** Validates that the selected quantity does not exceed the maximum allowed quantity. A maximum of 0 may indicate insufficient user balance to redeem the item. */
function maxQuantityValidator(maxQuantity: number) {
  return (group: AbstractControl): ValidationErrors | null => {
    const quantity = group.get('quantity')?.value;
    return quantity > maxQuantity ? { maxQuantityExceeded: { max: maxQuantity, actual: quantity } } : null;
  };
}
